<template>
  <div class="base-tab">
    <!-- Optional header slot -->
    <slot name="header" />

    <!-- PrimeVue Tabs wrapper with design system styling -->
    <Tabs
      :value="modelValue"
      :scrollable="shouldScroll"
      :lazy="!unmountInactive"
      @update:value="(value) => selectTab(String(value))"
    >
      <TabList class="base-tab__list" role="tablist">
        <Tab
          v-for="(tab, index) in tabs"
          :key="tab.value"
          :value="tab.value"
          :disabled="tab.disabled"
          class="base-tab__trigger"
          :class="getTabClasses(tab)"
          v-bind="getTabAttributes(tab)"
          @keydown="handleKeyDown($event, index)"
        >
          <span class="base-tab__label">{{ tab.label }}</span>
        </Tab>
      </TabList>

      <TabPanels class="base-tab__panels">
        <TabPanel
          v-for="tab in tabs"
          :id="`panel-${tab.value}`"
          :key="`panel-${tab.value}`"
          :value="tab.value"
          class="base-tab__panel"
          :aria-labelledby="`tab-${tab.value}`"
          role="tabpanel"
        >
          <slot
            :name="tab.value"
            :tab="tab"
            :isActive="isActiveTab(tab.value)"
          />
        </TabPanel>
      </TabPanels>
    </Tabs>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import Tabs from "primevue/tabs";
import TabList from "primevue/tablist";
import Tab from "primevue/tab";
import TabPanels from "primevue/tabpanels";
import TabPanel from "primevue/tabpanel";
import { useTabState } from "@/composables/useTabState";
import { useTabKeyboardNavigation } from "@/composables/useTabKeyboardNavigation";
import { useTabAccessibility } from "@/composables/useTabAccessibility";

// Types
export interface TabItem {
  label: string;
  value: string;
  disabled?: boolean;
}

interface Props {
  modelValue: string;
  tabs: TabItem[];
  unmountInactive?: boolean;
  scrollThreshold?: number;
}

// Props & Emits
const props = withDefaults(defineProps<Props>(), {
  unmountInactive: false,
  scrollThreshold: 3,
});

const emit = defineEmits(["update:modelValue", "change"]);

// Composables
const { enabledTabs, isActiveTab, selectTab } = useTabState(props, emit);
const { handleKeyDown } = useTabKeyboardNavigation(
  props,
  enabledTabs,
  selectTab,
);
const { getTabAttributes } = useTabAccessibility(props);

// Computed
const shouldScroll = computed(() => props.tabs.length > props.scrollThreshold);

// Methods
const getTabClasses = (tab: TabItem) => ({
  "base-tab__trigger--active": isActiveTab(tab.value),
  "base-tab__trigger--disabled": tab.disabled,
});
</script>

<style lang="scss" scoped>
.base-tab {
  width: 100%;
  font-family: "Montserrat", sans-serif;

  // Override PrimeVue Tabs component to be transparent
  :deep(.p-tabs) {
    background: transparent;
    min-width: 1050px;
  }

  &__list {
    position: relative;
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    background-color: #fff;
    width: fit-content;
    height: 48px;
    display: inline-flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: flex-start;
    padding: 0 0 3.8rem !important;
    gap: 8px;
    text-align: left;
    font-size: 12px;
    color: #1d1d1d;
    margin-top: 20px;
    margin-bottom: 44px;

    // Override PrimeVue TabList internals
    :deep(.p-tablist) {
      background: transparent;
      border: none;
      padding: 0 0 4rem;
      margin: 0;
      color: inherit;
      width: 100%;
      display: flex;
      gap: 8px;
      height: 100%;
      align-items: stretch;
    }

    :deep(.p-tablist-tab-list) {
      padding: 4px;
      border: none;
      display: inline-flex;
      flex-wrap: nowrap;
      gap: 8px;
    }

    :deep(.p-tablist-active-bar) {
      display: none !important;
    }

    // Override PrimeVue's nav styling
    :deep(.p-tablist-nav) {
      display: flex;
      gap: 8px;
      height: 100%;
      align-items: stretch;
      width: 100%;
    }

    // Override scroll buttons if present
    :deep(.p-tablist-prev-button),
    :deep(.p-tablist-next-button) {
      background: transparent;
      border: none;
      color: #1d1d1d;
      padding: 0 8px;

      &:hover {
        background-color: rgba(68, 27, 73, 0.05);
      }
    }
  }

  &__trigger {
    position: relative;
    cursor: pointer;
    user-select: none;
    transition: all 0.7s cubic-bezier(0.23, 1, 0.32, 1);
    background-color: transparent;
    color: #1d1d1d;
    border: none;
    border-radius: 4px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding: 16.5px 20px;
    overflow: hidden;
    outline: none;
    align-self: stretch;
    height: 100%;
    will-change: transform, background-color, box-shadow, color;
    backface-visibility: hidden;
    transform: translateZ(0);

    // Override PrimeVue Tab defaults
    :deep(.p-tab) {
      background: transparent !important;
      border: none !important;
      padding: 0 !important;
      margin: 0 !important;
      color: inherit !important;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    :deep(.p-tablist-active-bar) {
      display: none !important;
    }

    // Style PrimeVue's active tab state
    &:deep(.p-tab-active) {
      box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.05);
      background-color: #441b49;
      color: #fff;
      transform: translateY(-1px);

      .base-tab__label {
        color: #fff;
      }
    }

    &:focus-visible {
      outline: 2px solid #441b49;
      outline-offset: 2px;
    }

    &:hover:not(&--disabled):not(&--active) {
      background-color: rgba(68, 27, 73, 0.05);
      transform: translateY(-1px) translateZ(0);
      transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
      box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.08);
    }

    &:active:not(&--disabled) {
      transform: translateY(0) translateZ(0);
      transition: transform 0.15s cubic-bezier(0.23, 1, 0.32, 1);
    }

    &--active {
      box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.1);
      background-color: #441b49;
      color: #fff;
      transform: translateY(-2px) translateZ(0);
      transition: all 0.5s cubic-bezier(0.23, 1, 0.32, 1);

      .base-tab__label {
        color: #fff;
        transform: translateY(0);
        transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
      }
    }

    &--disabled {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }
  }

  &__label {
    text-transform: uppercase;
    font-weight: 600;
    font-size: 12px;
    white-space: nowrap;
    color: inherit;
    transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
    will-change: color, transform;
  }

  &__panels {
    margin-top: 0;
    position: relative;
    overflow: hidden;

    :deep(.p-tabpanels) {
      background: transparent;
      border: none;
      padding: 0;
      position: relative;
    }
  }

  &__panel {
    padding: 20px;
    animation: fluidSlideIn 0.8s cubic-bezier(0.23, 1, 0.32, 1);
    opacity: 0;
    transform: translateY(30px) translateZ(0);
    animation-fill-mode: forwards;
    backface-visibility: hidden;
    will-change: transform, opacity;

    :deep(.p-tabpanel) {
      background: transparent;
      border: none;
      padding: 10px;
    }

    // Stagger child elements for a more fluid feel
    > * {
      animation: fadeInUp 0.8s cubic-bezier(0.23, 1, 0.32, 1) forwards;
      opacity: 0;
      transform: translateY(20px);

      &:nth-child(1) {
        animation-delay: 0.1s;
      }

      &:nth-child(2) {
        animation-delay: 0.15s;
      }

      &:nth-child(3) {
        animation-delay: 0.2s;
      }

      &:nth-child(4) {
        animation-delay: 0.25s;
      }

      &:nth-child(n + 5) {
        animation-delay: 0.3s;
      }
    }
  }

  // Responsive design
  @media (max-width: 600px) {
    &__list {
      font-size: 10px;
      overflow-x: auto;
      scrollbar-width: none;
      -ms-overflow-style: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    &__label {
      font-size: 10px;
    }

    &__trigger {
      padding: 16.5px 12px;
    }
  }

  // Respect user's motion preferences
  @media (prefers-reduced-motion: reduce) {
    &__trigger {
      transition: none;
      transform: none !important;
    }

    &__label {
      transition: none;
      transform: none !important;
    }

    &__panel {
      animation: none;
      opacity: 1;
      transform: none !important;
      filter: none !important;

      > * {
        animation: none;
        opacity: 1;
        transform: none !important;
      }
    }
  }
}

// Animations
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInSlide {
  0% {
    opacity: 0;
    transform: translateY(20px) scale(0.98);
  }
  50% {
    opacity: 0.7;
    transform: translateY(10px) scale(0.99);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes fluidSlideIn {
  0% {
    opacity: 0;
    transform: translateY(30px) translateZ(0) scale(0.96);
    filter: blur(2px);
  }
  25% {
    opacity: 0.3;
    transform: translateY(20px) translateZ(0) scale(0.98);
    filter: blur(1px);
  }
  75% {
    opacity: 0.9;
    transform: translateY(5px) translateZ(0) scale(0.99);
    filter: blur(0.5px);
  }
  100% {
    opacity: 1;
    transform: translateY(0) translateZ(0) scale(1);
    filter: blur(0);
  }
}

@keyframes fadeInUp {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
